import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import axios from 'axios';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setToken, refreshUser } = useAuth();  // CRITICAL: Get setToken to update React state
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // CRITICAL FIX: Clear old tokens before new login
      // This fixes the issue where OAuth works in Incognito but requires 2 attempts in normal browser
      console.log('[OAuth Debug] Step 0: Clearing old auth state');
      localStorage.removeItem('token');
      
      try {
        // PERMANENT FIX: /auth/google/session now returns JWT directly
        // No cookie timing issues, no retries needed
        console.log('[OAuth Debug] Step 1: Calling /auth/google/session');
        console.log('[OAuth Debug] Session ID:', sessionId.substring(0, 10) + '...');
        
        const sessionResponse = await axios.post(
          `${API}/auth/google/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );
        
        console.log('[OAuth Debug] Step 2: Response received');
        console.log('[OAuth Debug] Response status:', sessionResponse.status);
        console.log('[OAuth Debug] Response data keys:', Object.keys(sessionResponse.data));
        console.log('[OAuth Debug] Has access_token:', !!sessionResponse.data.access_token);
        console.log('[OAuth Debug] Has user:', !!sessionResponse.data.user);
        console.log('[OAuth Debug] User email:', sessionResponse.data.user?.email);

        // If JWT returned directly - use it immediately
        if (sessionResponse.data.access_token) {
          console.log('[OAuth Debug] Step 3: JWT received, storing token');
          
          localStorage.setItem('token', sessionResponse.data.access_token);
          setToken(sessionResponse.data.access_token);
          await new Promise(resolve => setTimeout(resolve, 200));
          await refreshUser();
          navigate('/dashboard', { replace: true });
          return;
        }

        // Fallback: old flow (shouldn't happen with new backend)
        console.log('[OAuth Debug] Step 3: No JWT in response, falling back to session-to-token');
        const tokenResponse = await axios.post(
          `${API}/auth/session-to-token`,
          {},
          { withCredentials: true }
        );
        
        // Use setToken here too
        setToken(tokenResponse.data.access_token);
        
        // Same 100ms delay before navigation
        await new Promise(resolve => setTimeout(resolve, 100));
        
        try {
          navigate('/dashboard', { replace: true });
        } catch (navError) {
          console.error('[OAuth Debug] Navigation error (fallback flow):', navError);
          window.location.href = '/dashboard';
        }
        
      } catch (error) {
        // Enhanced error logging for debugging
        console.error('[OAuth Debug] ❌ ERROR occurred');
        console.error('[OAuth Debug] Error type:', error.constructor.name);
        console.error('[OAuth Debug] Error message:', error.message);
        console.error('[OAuth Debug] Response status:', error.response?.status);
        console.error('[OAuth Debug] Response data:', error.response?.data);
        console.error('[OAuth Debug] Response headers:', error.response?.headers);
        console.error('[OAuth Debug] Full error:', error);
        
        // No retries - if it fails, redirect to login
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4">
      <div className="text-center bg-white p-8 rounded-lg shadow-lg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">Completing Sign In</h2>
        <p className="text-sm text-gray-500 mt-2">Please wait...</p>
      </div>
    </div>
  );
}
